import { Fragment, useEffect, useMemo, useState } from 'react';
import { supabase } from '../supabase.js';
import { loadResearch, webLink } from './adminData.js';
import { EmailLink } from './ProviderProspects.jsx';
import { SortableTable } from './SortableTable.jsx';

// The Investors and Funding tabs: research lists (199 equity investors; 31 grants and loans with no personal guarantee) from the
// investor radar, kept as data files that load only when a tab opens. Each is a search, filters folded behind one button, and a
// table whose rows open in place to show the rest.

const asList = (value) => (Array.isArray(value) ? value : []);
const lower = (value) => String(value ?? '').toLowerCase();
/** Every value a filter can take, from the data, most common first. */
const choicesOf = (rows, valuesOf) => {
  const tally = new Map();
  for (const row of rows) for (const value of valuesOf(row)) tally.set(value, (tally.get(value) || 0) + 1);
  return [...tally].sort((a, b) => b[1] - a[1] || String(a[0]).localeCompare(String(b[0]))).map(([value]) => value);
};

/** A link out to a website, shown as its domain. */
function SiteLink({ url, label }) {
  const site = webLink(url);
  return site ? <a className="admin-ext" href={site.href} target="_blank" rel="noreferrer">{label || site.domain}</a> : '–';
}

/** The search, the Filters button and (when open) the dropdowns. `filters` is `[{ key, label, any, choices: [[value, words]] }]`. */
function Controls({ placeholder, query, onQuery, filters, chosen, onChoose, onClear }) {
  const [open, setOpen] = useState(false);
  const chosenCount = Object.values(chosen).filter(Boolean).length;
  return (
    <div className="prov-filters">
      <div className="prov-search">
        <input type="search" aria-label={placeholder} value={query} onChange={(event) => onQuery(event.target.value)} placeholder={placeholder}/>
        <button type="button" className={`prov-filter-toggle${chosenCount > 0 ? ' is-chosen' : ''}`} aria-expanded={open} onClick={() => setOpen((now) => !now)}>
          {chosenCount > 0 ? `Filters · ${chosenCount}` : 'Filters'}
        </button>
        {(query.trim() !== '' || chosenCount > 0) && <button type="button" className="admin-link" onClick={onClear}>Clear filters</button>}
      </div>
      {open && (
        <div className="prov-filter-row" role="group" aria-label="Filters">
          {filters.map((filter) => (
            <select key={filter.key} aria-label={filter.label} className={chosen[filter.key] ? 'is-chosen' : undefined} value={chosen[filter.key] || ''}
              onChange={(event) => onChoose(filter.key, event.target.value)}>
              <option value="">{filter.any}</option>
              {filter.choices.map(([value, words]) => <option key={value} value={value}>{words}</option>)}
            </select>
          ))}
        </div>
      )}
    </div>
  );
}

/** One research list ('investors', 'funding' or 'revenue'), read from the database when its tab opens. Only admins can read it. */
export function useResearch(kind) {
  const [state, setState] = useState({ rows: null, error: '' });
  useEffect(() => {
    let live = true;
    loadResearch(supabase, kind).then(
      (rows) => { if (live) setState({ rows: asList(rows), error: '' }); },
      (cause) => { if (live) setState({ rows: [], error: cause.message || 'Could not load the list. Please reload the page.' }); },
    );
    return () => { live = false; };
  }, [kind]);
  return state;
}

/** Search, filters and which row is open, shared by both tabs. `matches(row, chosen, words)` decides what shows. */
function useListing(rows, matches) {
  const [query, setQuery] = useState('');
  const [chosen, setChosen] = useState({});
  const [openId, setOpenId] = useState('');
  const shown = useMemo(() => {
    const words = lower(query).split(/\s+/).filter(Boolean);
    return (rows || []).filter((row) => matches(row, chosen, words));
  }, [rows, chosen, query, matches]);
  const rowProps = (row) => ({
    className: `admin-email-row${openId === row.id ? ' is-open' : ''}`,
    onClick: (event) => {
      const own = event.target.closest?.('a, button, input, select');
      if (own && !own.classList.contains('admin-email-toggle')) return;
      setOpenId(openId === row.id ? '' : row.id);
    },
  });
  const toggle = (row) => (
    <button type="button" className="admin-email-toggle" aria-expanded={openId === row.id} aria-label={`Show more about ${row.name}`}>
      <span aria-hidden="true">{openId === row.id ? '▾' : '▸'}</span>
    </button>
  );
  const controls = {
    query, onQuery: setQuery, chosen,
    onChoose: (key, value) => setChosen((now) => ({ ...now, [key]: value })),
    onClear: () => { setQuery(''); setChosen({}); },
  };
  return { shown, openId, rowProps, toggle, controls };
}

const everyWord = (words, text) => words.every((word) => text.includes(word));

// ---------------------------------------------------------------- Investors

const OUR_SECTORS = 6;

const investorText = (row) => lower([
  row.name, row.alias, row.type, row.hq, row.thesis, ...asList(row.sectors), ...asList(row.stages),
  ...asList(row.partners).map((partner) => `${partner.name} ${partner.role}`), ...asList(row.portfolio).map((company) => company.name),
].join(' '));

function matchesInvestor(row, chosen, words) {
  if (chosen.type && row.type !== chosen.type) return false;
  if (chosen.stage && !asList(row.stages).includes(chosen.stage)) return false;
  if (chosen.sector && !asList(row.sectors).includes(chosen.sector)) return false;
  if (chosen.based && (chosen.based === 'uk') !== Boolean(row.uk)) return false;
  return everyWord(words, investorText(row));
}

/** Everything else about an investor, opened under its line. */
function InvestorDetail({ row }) {
  const partners = asList(row.partners);
  const portfolio = asList(row.portfolio);
  return (
    <div className="research-detail">
      {row.thesis && <p className="research-lead">{row.thesis}</p>}
      <div className="prov-details">
        <dl>
          <dt>Sectors</dt><dd>{asList(row.sectors).join(', ') || '–'}</dd>
          <dt>Stages</dt><dd>{asList(row.stages).join(', ') || '–'}</dd>
          <dt>Cheque</dt><dd>{row.cheque || '–'}</dd>
          <dt>Based</dt><dd>{row.hq || '–'}</dd>
        </dl>
        <dl>
          <dt>Website</dt><dd><SiteLink url={row.website}/></dd>
          <dt>Pitch</dt><dd><SiteLink url={row.pitch_url} label="Where to pitch"/></dd>
          <dt>Email</dt>
          <dd>{asList(row.emails).length ? asList(row.emails).map((email, index) => <Fragment key={email}>{index > 0 && ', '}<EmailLink value={email}/></Fragment>) : '–'}</dd>
        </dl>
        <dl>
          <dt>People</dt>
          <dd>{partners.length ? <ul className="research-list">{partners.map((person) => <li key={person.name}>{person.name}<span className="admin-sub">{person.role}</span></li>)}</ul> : '–'}</dd>
        </dl>
      </div>
      {portfolio.length > 0 && (
        <>
          <h3 className="admin-subsection">Portfolio</h3>
          <ul className="research-list research-columns">{portfolio.map((company) => <li key={company.name}><strong>{company.name}</strong>{company.note && ` · ${company.note}`}</li>)}</ul>
        </>
      )}
    </div>
  );
}

/** 199 equity investors: who they are, what they back and how far our sectors overlap with theirs, best fit first. */
export function InvestorsTab() {
  const { rows, error } = useResearch('investors');
  const sorted = useMemo(() => (rows ? [...rows].sort((a, b) => (b.overlap || 0) - (a.overlap || 0) || a.name.localeCompare(b.name, 'en-GB')) : null), [rows]);
  const { shown, openId, rowProps, toggle, controls } = useListing(sorted, matchesInvestor);
  if (error) return <p className="mod-error" role="alert">{error}</p>;
  if (!sorted) return <p>Loading investors…</p>;
  const filters = [
    { key: 'type', label: 'Type', any: 'Any type', choices: choicesOf(sorted, (row) => [row.type]).map((value) => [value, value]) },
    { key: 'stage', label: 'Stage', any: 'Any stage', choices: choicesOf(sorted, (row) => asList(row.stages)).map((value) => [value, value]) },
    { key: 'sector', label: 'Sector', any: 'Any sector', choices: choicesOf(sorted, (row) => asList(row.sectors)).map((value) => [value, value]) },
    { key: 'based', label: 'Based', any: 'Anywhere', choices: [['uk', 'In the UK'], ['abroad', 'Outside the UK']] },
  ];
  const columns = [
    { key: 'name', label: 'Investor', value: (row) => row.name, cell: (row) => <span className="admin-nowrap">{toggle(row)}<span className="research-clip" title={row.name}>{row.name}</span></span> },
    { key: 'type', label: 'Type', value: (row) => row.type, cell: (row) => row.type },
    { key: 'stages', label: 'Stages', value: (row) => asList(row.stages).join(', '), cell: (row) => asList(row.stages).join(', ') || '–' },
    { key: 'cheque', label: 'Cheque', value: (row) => row.cheque, cell: (row) => row.cheque || '–' },
    { key: 'overlap', label: 'Our sectors', value: (row) => row.overlap || 0, first: 'desc', cell: (row) => `${row.overlap || 0} of ${OUR_SECTORS}` },
    { key: 'hq', label: 'Based', value: (row) => row.hq, cell: (row) => row.hq || '–' },
  ];
  return (
    <>
      <Controls placeholder="Search investors, people or portfolio" filters={filters} {...controls}/>
      {shown.length === 0
        ? <p className="admin-empty">No investors match.</p>
        : <SortableTable columns={columns} rows={shown} rowKey={(row) => row.id} rowProps={rowProps} detail={(row) => (openId === row.id ? <InvestorDetail row={row}/> : null)}/>}
    </>
  );
}

// ---------------------------------------------------------------- Funding


const fundingText = (row) => lower([
  row.name, row.provider, row.type, row.instrument, row.amount, row.pg_status, row.fit, row.requirements, ...asList(row.sectors),
].join(' '));

/** A grant, or a loan with no personal guarantee. A loan "secured on grant receivables" is still a loan, so only non-dilutive grants count. */
const guaranteeOf = (row) => (/^non-dilutive grant/i.test(row.pg_status) ? 'grant' : 'no-pg');

function matchesFunding(row, chosen, words) {
  if (chosen.type && row.type !== chosen.type) return false;
  if (chosen.kind && guaranteeOf(row) !== chosen.kind) return false;
  if (chosen.preRevenue && (chosen.preRevenue === 'yes') !== Boolean(row.nil_revenue_ok)) return false;
  return everyWord(words, fundingText(row));
}

/** Everything else about a grant or loan, opened under its line. */
function FundingDetail({ row }) {
  const contacts = asList(row.contacts);
  const examples = asList(row.examples);
  return (
    <div className="research-detail">
      {row.fit && <p className="research-lead">{row.fit}</p>}
      <div className="prov-details">
        <dl>
          <dt>What it is</dt><dd>{row.instrument || '–'}</dd>
          <dt>Guarantee</dt><dd>{row.pg_status || '–'}</dd>
          <dt>Pre-revenue</dt><dd>{row.nil_revenue_ok ? 'Yes' : 'No'}</dd>
          <dt>Decision in</dt><dd>{row.turnaround || '–'}</dd>
          <dt>Where</dt><dd>{row.hq || '–'}</dd>
        </dl>
        <dl>
          <dt>Apply</dt><dd><SiteLink url={row.apply_url} label="Application page"/></dd>
          <dt>Website</dt><dd><SiteLink url={row.website}/></dd>
          <dt>Email</dt>
          <dd>{asList(row.emails).length ? asList(row.emails).map((email, index) => <Fragment key={email}>{index > 0 && ', '}<EmailLink value={email}/></Fragment>) : '–'}</dd>
        </dl>
        <dl>
          <dt>Contacts</dt>
          <dd>{contacts.length ? <ul className="research-list">{contacts.map((person) => <li key={person.name}>{person.name}<span className="admin-sub">{person.role}</span></li>)}</ul> : '–'}</dd>
        </dl>
      </div>
      {row.requirements && (
        <>
          <h3 className="admin-subsection">What it needs</h3>
          <p className="research-text">{row.requirements}</p>
        </>
      )}
      {examples.length > 0 && (
        <>
          <h3 className="admin-subsection">Who it has funded</h3>
          <ul className="research-list research-columns">{examples.map((example) => <li key={example.name}><strong>{example.name}</strong>{example.note && ` · ${example.note}`}</li>)}</ul>
        </>
      )}
    </div>
  );
}

/** 31 grants and loans with no personal guarantee: how much, on what terms, how fast, and whether we qualify before revenue. */
export function FundingTab() {
  const { rows, error } = useResearch('funding');
  const { shown, openId, rowProps, toggle, controls } = useListing(rows, matchesFunding);
  if (error) return <p className="mod-error" role="alert">{error}</p>;
  if (!rows) return <p>Loading funding…</p>;
  const filters = [
    { key: 'type', label: 'Type', any: 'Any type', choices: choicesOf(rows, (row) => [row.type]).map((value) => [value, value]) },
    { key: 'kind', label: 'Grant or loan', any: 'Grants and loans', choices: [['grant', 'Grants'], ['no-pg', 'Loans, no personal guarantee']] },
    { key: 'preRevenue', label: 'Pre-revenue', any: 'Any stage', choices: [['yes', 'Open before revenue'], ['no', 'Needs revenue']] },
  ];
  const columns = [
    { key: 'name', label: 'Programme', value: (row) => row.name, cell: (row) => <span className="admin-nowrap">{toggle(row)}<span className="research-clip" title={row.name}>{row.name}</span></span> },
    { key: 'provider', label: 'From', value: (row) => row.provider, cell: (row) => row.provider || '–' },
    { key: 'type', label: 'Type', value: (row) => row.type, cell: (row) => row.type },
    { key: 'amount', label: 'Amount', value: (row) => row.amount, cell: (row) => row.amount || '–' },
    { key: 'guarantee', label: 'Guarantee', value: (row) => row.pg_status, cell: (row) => row.pg_status || '–' },
    { key: 'preRevenue', label: 'Pre-revenue', value: (row) => (row.nil_revenue_ok ? 1 : 0), first: 'desc', cell: (row) => (row.nil_revenue_ok ? 'Yes' : 'No') },
    { key: 'turnaround', label: 'Decision in', value: (row) => row.turnaround, cell: (row) => row.turnaround || '–' },
  ];
  return (
    <>
      <Controls placeholder="Search grants and loans" filters={filters} {...controls}/>
      {shown.length === 0
        ? <p className="admin-empty">No grants or loans match.</p>
        : <SortableTable columns={columns} rows={shown} rowKey={(row) => row.id} rowProps={rowProps} detail={(row) => (openId === row.id ? <FundingDetail row={row}/> : null)}/>}
    </>
  );
}
